import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lightbulb, Loader2, FileText, Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ReactMarkdown from "react-markdown";

export default function LumiReport() {
  const [period, setPeriod] = useState("last_30");
  const [isGenerating, setIsGenerating] = useState(false);
  const [report, setReport] = useState("");
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  const reportRef = useRef<HTMLDivElement>(null);

  const generateReport = async () => {
    setIsGenerating(true);
    setReport("");

    try {
      const response = await fetch("/api/admin/lumi/generate-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ period }),
      });

      if (!response.ok) throw new Error("Failed");

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No stream");

      const decoder = new TextDecoder();
      let content = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        for (const line of chunk.split("\n")) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) {
                content += data.content;
                setReport(content);
              }
              if (data.done) break;
            } catch {}
          }
        }
      }
    } catch {
      toast({ title: "Failed to generate report", variant: "destructive" });
    } finally {
      setIsGenerating(false);
    }
  };

  const copyReport = () => {
    navigator.clipboard.writeText(report);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: "Report copied to clipboard" });
  };

  return (
    <Card className="border-none shadow-sm" data-testid="lumi-report-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
              <Lightbulb className="w-4 h-4 text-white" />
            </div>
            <CardTitle className="text-base">AI Report Generator</CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px]" data-testid="select-report-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_7">Last 7 Days</SelectItem>
              <SelectItem value="last_30">Last 30 Days</SelectItem>
              <SelectItem value="last_90">Last 90 Days</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={generateReport}
            disabled={isGenerating}
            className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
            data-testid="btn-generate-report"
          >
            {isGenerating ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</>
            ) : (
              <><FileText className="w-4 h-4 mr-2" /> Generate Report</>
            )}
          </Button>
          {report && !isGenerating && (
            <Button variant="outline" size="sm" onClick={copyReport} data-testid="btn-copy-report">
              {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          )}
        </div>

        {report && (
          <div ref={reportRef} className="prose prose-sm dark:prose-invert max-w-none p-6 rounded-xl bg-muted/50 border" data-testid="report-content">
            <ReactMarkdown>{report}</ReactMarkdown>
          </div>
        )}

        {!report && !isGenerating && (
          <div className="p-8 text-center text-muted-foreground">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Select a period and generate an AI-powered narrative report</p>
            <p className="text-xs mt-1">Perfect for sharing with property owners and investors</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
